package com.advaita.cloud.drive.shared

import kotlinx.serialization.Serializable

/** Customer AWS keys for one private bucket in a pull batch. Server-side only, never persisted in the clear. */
@Serializable
data class S3Credential(
    val bucket: String,
    val accessKeyId: String,
    val secretAccessKey: String,
    val sessionToken: String? = null,
)

/** POST /api/pull body — pull [urls] into the folder at [parentPath]. */
@Serializable
data class PullRequest(
    /** The caller's own destination path. Null (with no [parentId]) means the caller's root. */
    val parentPath: String? = null,
    val urls: List<String>,
    val s3Credentials: List<S3Credential> = emptyList(),   // one per distinct s3:// bucket
    /** ADV-1329: a destination folder by id, which may be shared. Exclusive with [parentPath]. */
    val parentId: String? = null,
)

/** Response — the created drive_file ids (one per accepted URL), in request order. */
@Serializable
data class PullResponse(val fileIds: List<String>)

/**
 * POST /api/pull/study body — the iScanGuide integration.
 *
 * ISG mints [url] itself (its own export link for [studyId]) and posts it with the
 * end user's Drive bearer token; org/team/owner come from that token, so the file
 * lands in the caller's own Drive exactly as a normal pull would.
 *
 * [studyId] is not redundant with the id inside [url]: every ISG export URL shares
 * the path /api/sample/export, so URL-derived naming would call every study "export".
 * It names the file and correlates the pull back to the study. [fileName] lets ISG
 * supply the study's real title instead of the "study-<id>" default.
 */
@Serializable
data class PullStudyRequest(
    /** The caller's own destination path. Null (with no [parentId]) means the caller's root. */
    val parentPath: String? = null,
    val studyId: String,
    val url: String,
    val fileName: String? = null,
    /** ADV-1329: a destination folder by id, which may be shared. Exclusive with [parentPath]. */
    val parentId: String? = null,
)

/**
 * Response to a study pull. ASYNC: 202 means the transfer was accepted and queued,
 * NOT that any bytes moved. Poll GET /api/pull/{fileId} until [status] is terminal.
 */
@Serializable
data class PullStudyResponse(val fileId: String, val status: String)

/** Compact per-file pull progress folded onto [FileDto] for the file listing. */
@Serializable
data class PullProgressDto(
    val status: String,          // PENDING | RUNNING | SUCCEEDED | FAILED | CANCELLED
    val pct: Int? = null,
    val bytesDone: Long? = null,
    val sizeBytes: Long? = null,
    val error: String? = null,
)
